import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import type { CommandPaletteRecentActionsStore, CommandPaletteRecentActionsMap } from "./types";

const MAX_STORED_ACTIONS = 50;
const MAX_TIMESTAMPS_PER_ACTION = 10;

// Remove oldest actions when exceeding max limit
const cleanupOldestActions = (actionUsage: CommandPaletteRecentActionsMap) => {
  const entries = Object.entries(actionUsage);
  const sortedByRecency = [...entries].sort(([, a], [, b]) => b.usage.lastUsed - a.usage.lastUsed);
  return Object.fromEntries(sortedByRecency.slice(0, MAX_STORED_ACTIONS));
};

const useCommandPaletteRecentActionsStore = create<CommandPaletteRecentActionsStore>()(
  persist(
    immer((set) => ({
      actionUsage: {},

      trackActionUsage: (action) =>
        set((state) => {
          const now = Date.now();
          const existing = state.actionUsage[action.id];

          if (existing) {
            // Update existing: increment count, refresh timestamp, add to timestamps
            existing.usage.count += 1;
            existing.usage.lastUsed = now;
            existing.usage.timestamps = [now, ...existing.usage.timestamps].slice(
              0,
              MAX_TIMESTAMPS_PER_ACTION
            );
          } else {
            // New action: initialize with count=1
            state.actionUsage[action.id] = {
              ...action,
              usage: { count: 1, lastUsed: now, timestamps: [now] },
            };
          }

          // Cleanup only when exceeding max limit
          if (Object.keys(state.actionUsage).length > MAX_STORED_ACTIONS) {
            state.actionUsage = cleanupOldestActions(state.actionUsage);
          }
        }),

      removeAction: (actionId) =>
        set((state) => {
          delete state.actionUsage[actionId];
        }),

      clearActionUsage: () =>
        set((state) => {
          state.actionUsage = {};
        }),
    })),
    {
      name: "command-palette-recent-actions",
      partialize: (state) => ({ actionUsage: state.actionUsage }),
    }
  )
);

export default useCommandPaletteRecentActionsStore;
